import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import * as authApi from '../api/auth'
import { wsManager } from '../api/websocket'

export function useAuth() {
  const navigate = useNavigate()
  const { user, isAuthenticated, login: storeLogin, logout: storeLogout } = useAuthStore()

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await authApi.login({ email, password })

      storeLogin(
        {
          id: response.user_id,
          email: response.email || email,
          displayName: response.display_name,
          avatarUrl: response.avatar_url,
          telegramLinked: response.telegram_linked,
          isAdmin: response.is_admin,
          role: response.role as 'user' | 'clinician' | 'admin' | undefined,
        },
        response.access_token,
        response.refresh_token,
        'email'
      )

      // Connect WebSocket
      wsManager.connect()

      navigate('/chat')
    },
    [storeLogin, navigate]
  )

  const register = useCallback(
    async (
      email: string,
      password: string,
      name?: string,
      birthDate?: string,
      gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say'
    ) => {
      const response = await authApi.register({
        email,
        password,
        name,
        birth_date: birthDate,
        gender,
      })

      // Registration successful - redirect to verify email page
      // User must verify email before they can login
      navigate('/verify-email', { state: { email: response.email } })
    },
    [navigate]
  )

  const telegramLogin = useCallback(
    async (authData: Parameters<typeof authApi.telegramLogin>[0]) => {
      const response = await authApi.telegramLogin(authData)

      storeLogin(
        {
          id: response.user_id,
          email: response.email,
          displayName: response.display_name || authData.first_name,
          avatarUrl: authData.photo_url, // Save Telegram avatar
          telegramLinked: true,
          isAdmin: response.is_admin,
          role: response.role as 'user' | 'clinician' | 'admin' | undefined,
        },
        response.access_token,
        response.refresh_token,
        'telegram'
      )

      // Connect WebSocket
      wsManager.connect()

      navigate('/chat')
    },
    [storeLogin, navigate]
  )

  const googleLogin = useCallback(
    async (credential: string) => {
      const response = await authApi.googleLogin(credential)

      storeLogin(
        {
          id: response.user_id,
          email: response.email,
          displayName: response.display_name,
          avatarUrl: response.avatar_url, // Save Google avatar if returned
          telegramLinked: response.telegram_linked,
          isAdmin: response.is_admin,
          role: response.role as 'user' | 'clinician' | 'admin' | undefined,
        },
        response.access_token,
        response.refresh_token,
        'google'
      )

      // Connect WebSocket
      wsManager.connect()

      navigate('/chat')
    },
    [storeLogin, navigate]
  )

  const logout = useCallback(async () => {
    const refreshToken = useAuthStore.getState().refreshToken
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken)
      } catch {
        // Ignore errors during logout
      }
    }

    // Disconnect WebSocket
    wsManager.disconnect()

    // Clear stores
    storeLogout()
    useChatStore.getState().clearMessages()

    navigate('/login')
  }, [storeLogout, navigate])

  return {
    user,
    isAuthenticated,
    login,
    register,
    telegramLogin,
    googleLogin,
    logout,
  }
}
